/**
 * Author-set default values for form controls (CWL-470), plus the mapping-key
 * normalisation auto-populate matches on (CWL-303).
 *
 * Shared so the works-lite builder (which blocks save on an invalid default) and the
 * backend pre-fill (which skips one) apply exactly the same rule.
 */

/** Lowercase, collapse every non-alphanumeric run to `_`, strip edge `_`. */
export function normaliseMappingKey(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const normalised = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalised.length > 0 ? normalised : null;
}

export const DEFAULT_VALUE_MAX_LENGTH = 1000;

/** Control types that can be pre-filled from a default (date/time excluded — CWL-470 D2; checkbox takes a list — QA). */
export const DefaultValueControlType = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  LONG_TEXTAREA: 'long_textarea',
  NUMBER: 'number',
  TEL: 'tel',
  EMAIL: 'email',
  URL: 'url',
  RADIO: 'radio',
  OPTION: 'option',
  CONDITIONAL_FIELD: 'conditional_field',
  CHECKBOX: 'checkbox',
} as const;
export type DefaultValueControlType =
  (typeof DefaultValueControlType)[keyof typeof DefaultValueControlType];

const DEFAULT_VALUE_CONTROL_TYPES: readonly string[] = Object.values(DefaultValueControlType);

export function supportsDefaultValue(controlType: unknown): controlType is DefaultValueControlType {
  return typeof controlType === 'string' && DEFAULT_VALUE_CONTROL_TYPES.includes(controlType);
}

export const DefaultValueIssue = {
  UNSUPPORTED_TYPE: 'unsupported_type',
  TOO_LONG: 'too_long',
  NOT_A_NUMBER: 'not_a_number',
  INVALID_EMAIL: 'invalid_email',
  INVALID_URL: 'invalid_url',
  INVALID_TEL: 'invalid_tel',
  NOT_A_CHOICE: 'not_a_choice',
} as const;
export type DefaultValueIssue = (typeof DefaultValueIssue)[keyof typeof DefaultValueIssue];

/** The fields the check reads. Typed loosely: form JSON is untyped legacy data. */
export interface DefaultValueControl {
  control_type?: unknown;
  default_value?: unknown;
  options?: ReadonlyArray<{ option?: unknown } | null | undefined> | null;
  conditions?: ReadonlyArray<{ condition_name?: unknown } | null | undefined> | null;
}

export type DefaultValueCheck =
  | { kind: 'none' }
  | { kind: 'valid'; value: string }
  | { kind: 'valid_choices'; values: string[] }
  | { kind: 'invalid'; issue: DefaultValueIssue };

const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_PATTERN = /^https?:\/\/[^\s/?#]+\.[^\s]+$/i;
const TEL_PATTERN = /^\+?[\d\s()-]{3,20}$/;

const invalid = (issue: DefaultValueIssue): DefaultValueCheck => ({ kind: 'invalid', issue });

/** Exact (trimmed) match first, so options differing only by case keep their own text. */
function matchChoice(wanted: string, choices: readonly unknown[]): string | null {
  const texts = choices.filter((choice): choice is string => typeof choice === 'string');
  const exact = texts.find((choice) => choice.trim() === wanted);
  if (exact !== undefined) return exact;
  const target = wanted.toLowerCase();
  return texts.find((choice) => choice.trim().toLowerCase() === target) ?? null;
}

function checkPattern(value: string, pattern: RegExp, issue: DefaultValueIssue): DefaultValueCheck {
  return pattern.test(value) ? { kind: 'valid', value } : invalid(issue);
}

function checkChoice(value: string, choices: readonly unknown[]): DefaultValueCheck {
  const match = matchChoice(value, choices);
  return match === null ? invalid(DefaultValueIssue.NOT_A_CHOICE) : { kind: 'valid', value: match };
}

/** Checkbox (CWL-470 QA): `default_value` lists the ticked options; a lone string is one tick. */
function checkCheckboxDefault(control: DefaultValueControl): DefaultValueCheck {
  const raw = control.default_value;
  const listed: unknown[] = Array.isArray(raw) ? raw : [raw];
  const entries = listed
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  if (entries.length === 0) return { kind: 'none' };
  const choices = (control.options ?? []).map((option) => option?.option);
  const values: string[] = [];
  for (const entry of entries) {
    const match = matchChoice(entry, choices);
    if (match === null) return invalid(DefaultValueIssue.NOT_A_CHOICE);
    if (!values.includes(match)) values.push(match);
  }
  return { kind: 'valid_choices', values };
}

export function checkDefaultValue(control: DefaultValueControl): DefaultValueCheck {
  if (control.control_type === DefaultValueControlType.CHECKBOX)
    return checkCheckboxDefault(control);
  const raw = control.default_value;
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value.length === 0) return { kind: 'none' };
  const controlType = control.control_type;
  if (!supportsDefaultValue(controlType)) return invalid(DefaultValueIssue.UNSUPPORTED_TYPE);
  if (value.length > DEFAULT_VALUE_MAX_LENGTH) return invalid(DefaultValueIssue.TOO_LONG);

  switch (controlType) {
    case DefaultValueControlType.NUMBER:
      return checkPattern(value, NUMBER_PATTERN, DefaultValueIssue.NOT_A_NUMBER);
    case DefaultValueControlType.EMAIL:
      return checkPattern(value, EMAIL_PATTERN, DefaultValueIssue.INVALID_EMAIL);
    case DefaultValueControlType.URL:
      return checkPattern(value, URL_PATTERN, DefaultValueIssue.INVALID_URL);
    case DefaultValueControlType.TEL:
      return checkPattern(value, TEL_PATTERN, DefaultValueIssue.INVALID_TEL);
    case DefaultValueControlType.RADIO:
    case DefaultValueControlType.OPTION:
      return checkChoice(
        value,
        (control.options ?? []).map((option) => option?.option),
      );
    case DefaultValueControlType.CONDITIONAL_FIELD:
      return checkChoice(
        value,
        (control.conditions ?? []).map((condition) => condition?.condition_name),
      );
    default:
      return { kind: 'valid', value };
  }
}

/** The `value` a valid default writes (CWL-470), or null when there is none or it fails the rule. */
export function defaultAnswerFor(control: DefaultValueControl): string[] | null {
  const check = checkDefaultValue(control);
  if (check.kind === 'valid') return [check.value];
  if (check.kind === 'valid_choices') return check.values;
  return null;
}
